import { FC, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form@latest';
import StepLinkList from '@/components/UI/StepLinkList';
import { SamplePlanTable } from '../SamplePlanTable';
import { SamplePlanTableProvider, useSamplePlanTableContext } from '../SamplePlanTableProvider';
import { RouteService } from '@/support/RouteService.ts';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { errorToast, successToast } from '@/helpers.tsx';
import DateTimePicker from '@/components/UI/FormFields/Latest/DateTimePicker';
import Button from '@/components/UI/Button';
import Loading from '@/components/Loading/Loading';
import { StudyApiId } from '@/model/Study.model.ts';
import { GroupApiId, TreatmentGroup } from '@/model/TreatmentGroup.model';
import {
  GroupTarget,
  PlannedAnimalTarget,
  RegisteredAnimalTarget,
  SampleDefinitionApiId,
  SampleDefinitionFormData,
} from '@/model/SamplePlan.model';
import { Color, ISODateTime } from '@/model/Common.model';
import { TeamGlossary } from '@/model/Glosary.model';
import { DateInputUtils, DateUtils, TimeUtils } from '@/utils/Date.utils.ts';
import Banner from '@/components/UI/Banner';
import { AnimalApiId } from '@/model/Animal.model.ts';
import { AnimalDefinitionApiId } from '@/model/PlannedAnimal.model.ts';
import _isEmpty from 'lodash/isEmpty';
import { _notNil } from '@/littledash.ts';
import Header from '@/components/UI/Header';
import {
  buildRegisteredAnimalsMap,
  buildSampleDefinitionsMap,
  generateSamplesPlanRoute,
  generateStudySamplesRoute,
  isLoadingRequiredData,
  hasValidExecutionState,
} from './ExecuteSamples.utils';

interface ExecuteSamplesCollectStepProps {
  testId?: string;
}

interface FormData {
  date: string;
  comments: string;
}

interface SamplePlanTableWithBannerProps {
  selectedAnimals: Set<AnimalApiId>;
  tableData: {
    sample_definitions: Array<SampleDefinitionFormData>;
    groups: Array<GroupTarget>;
    registered_animals: Array<RegisteredAnimalTarget>;
    planned_animals: Array<PlannedAnimalTarget>;
  };
  registeredAnimalsResponse: any;
  animalIdDefinitionMap: Map<AnimalApiId, AnimalDefinitionApiId>;
}

// Component that has access to SamplePlanTableContext
const SamplePlanTableWithBanner: FC<SamplePlanTableWithBannerProps> = ({
  selectedAnimals,
  tableData,
  registeredAnimalsResponse,
  animalIdDefinitionMap,
}) => {
  const { sample_definition_rows } = useSamplePlanTableContext();

  // Calculate total samples that will be collected using same logic as sampleDefinitionsMap
  const totalSamplesToCollect = useMemo(() => {
    if (selectedAnimals.size === 0 || !tableData || registeredAnimalsResponse?.type !== 'success') {
      return 0;
    }

    const registeredAnimalsMap = buildRegisteredAnimalsMap(registeredAnimalsResponse);
    const sampleDefinitionsMap = buildSampleDefinitionsMap(
      selectedAnimals,
      tableData.sample_definitions,
      registeredAnimalsMap,
      animalIdDefinitionMap
    );

    // Calculate total samples: for each sample definition, multiply eligible animals by timepoints
    return Object.entries(sampleDefinitionsMap).reduce((total, [sampleDefId, eligibleAnimals]) => {
      const sampleDefinitionRow = sample_definition_rows.get(sampleDefId as SampleDefinitionApiId);
      if (!sampleDefinitionRow) {
        return total;
      }

      // Calculate samples for this definition based on timepoints
      const timepointsArray = sampleDefinitionRow.timepoints ? sampleDefinitionRow.timepoints.split(',') : [];
      const timepointsCount = timepointsArray.length ?? 0;
      const samplesPerAnimal = timepointsCount <= 1 ? 1 : timepointsCount;

      // Total samples for this definition = eligible animals × samples per animal
      return total + eligibleAnimals.length * samplesPerAnimal;
    }, 0);
  }, [selectedAnimals, tableData, registeredAnimalsResponse, animalIdDefinitionMap, sample_definition_rows]);

  return (
    <>
      {totalSamplesToCollect > 0 && (
        <div className="mb4">
          <Banner info className="mw6 mr4 mb4" dismiss={false}>
            <p className="lh-copy">
              <strong className="mr1">
                {totalSamplesToCollect} sample{totalSamplesToCollect === 1 ? '' : 's'}
              </strong>
              will be collected upon submission
            </p>
          </Banner>
        </div>
      )}
      <SamplePlanTable />
    </>
  );
};

const ExecuteSamplesCollectStep: FC<ExecuteSamplesCollectStepProps> = ({ testId = 'execute-samples-collect-step' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: studyId } = useParams<{ id: string }>();
  const defaultDate: ISODateTime = DateUtils.createDateTimeInUserTimezone(
    DateUtils.dateNow(),
    TimeUtils.currentTimeInUserTimezone()
  );

  // Parse data from location state
  const { studyApiId, selectedAnimals, sampleDefinitionIds, animalIdDefinitionMap } = useMemo(() => {
    const stateData = location.state;

    const studyApiId = stateData?.studyApiId as StudyApiId;
    const selectedAnimals = stateData?.selectedAnimals
      ? new Set(stateData.selectedAnimals as Array<AnimalApiId>)
      : new Set<AnimalApiId>();
    const sampleDefinitionIds = stateData?.sampleDefinitionIds ?? [];
    const animalIdDefinitionMap = stateData?.animalIdDefinitionMap ?? new Map<AnimalApiId, AnimalDefinitionApiId>();

    return { studyApiId, selectedAnimals, sampleDefinitionIds, animalIdDefinitionMap };
  }, [location.state]);

  // Generate back link to sample table
  const backLink = useMemo(() => generateStudySamplesRoute(studyId!), [studyId]);
  const samplesPlanRoute = useMemo(() => generateSamplesPlanRoute(studyId!), [studyId]);

  // Step form configuration
  const steps = useMemo(
    () => [
      {
        title: 'Select animals',
        complete: true, // Previous step is complete
      },
      {
        title: 'Collect samples',
        complete: false, // Currently on this step
      },
    ],
    []
  );

  const currentStep = 2; // Currently on step 2 (Collect samples)

  const formMethods = useForm<FormData>({
    defaultValues: {
      date: DateInputUtils.toLocalDateTime(defaultDate),
      comments: '',
    },
  });

  // Watch the date field to enable/disable the save button
  const watchedDate = formMethods.watch('date');

  // API hooks for loading table data - similar to SamplePlanTableView
  const { response: groupsResponse, loading: groupsLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.groups.list',
    path: { id: studyId },
    options: { invokeOnInit: true },
  });

  const { response: sampleMetadataGlossaryResponse } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: false,
      filter_type: 'sample_meta',
    },
    invokeOnInit: true,
  });

  const { response: sampleGlossaryResponse } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    path: undefined,
    query: {
      group: 'samples',
      perPage: -1,
      order: 'asc',
      sort: 'title',
    },
    invokeOnInit: true,
    options: { onError: { slug: 'samples-glossary-load' } },
  });

  const {
    loading: samplePlanLoading,
    response: samplePlanResponse,
    invoke: loadSamplePlan,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples',
    invokeOnInit: false,
  });

  const {
    loading: registeredAnimalsLoading,
    response: registeredAnimalsResponse,
    invoke: loadRegisteredAnimals,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples/animals',
    invokeOnInit: false,
  });

  // Load data when studyApiId is available
  useEffect(() => {
    if (_notNil(studyApiId)) {
      loadSamplePlan({ path: { studyApiId } });
      loadRegisteredAnimals({
        path: { studyApiId },
        query: {
          definitions: sampleDefinitionIds,
          collection_type: 'collected',
        },
      });
    }
  }, [studyApiId, sampleDefinitionIds]);

  // Process table data - similar to SamplePlanTableView
  const tableData = useMemo(() => {
    if (
      groupsResponse?.type === 'success' &&
      registeredAnimalsResponse?.type === 'success' &&
      samplePlanResponse?.type === 'success'
    ) {
      const groups = (
        groupsResponse.body as {
          data: Array<TreatmentGroup>;
        }
      ).data.map<GroupTarget>((group) => ({
        api_id: group.api_id as GroupApiId,
        name: group.name,
        no: group.no,
        color: group.color as Color,
        max_subjects: group.max_subjects,
      }));

      // Filter registered animals to only include selected ones
      const registeredAnimalsData = registeredAnimalsResponse?.body?.data ?? [];
      const registered_animals = registeredAnimalsData
        .filter((animal) => selectedAnimals.has(animal.api_id))
        .map<RegisteredAnimalTarget>((animal) => ({
          api_id: animal.api_id,
          name: animal.name,
          study_group_api_id: _notNil(animal.study_group) ? animal.study_group.api_id : null,
        }));

      // Filter sample definitions to only include selected ones
      const filteredSampleDefinitions = samplePlanResponse.body.definitions.filter((def) =>
        sampleDefinitionIds.includes(def.api_id)
      );

      return {
        groups,
        registered_animals,
        sample_definitions: filteredSampleDefinitions,
        planned_animals: [],
      };
    }
    return null;
  }, [groupsResponse, registeredAnimalsResponse, samplePlanResponse, sampleDefinitionIds, selectedAnimals]);

  const { loading: isCreating, invoke: executeCreateSamples } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/planned/samples/execute',
    path: { studyApiId },
    invokeOnInit: false,
    options: {
      onError: { toast: true },
    },
  });

  const handleSaveAndFinish = useCallback(
    (formData: FormData) => {
      if (!studyApiId || _isEmpty(selectedAnimals)) {
        errorToast('No animals selected. Please go back and select animals.');
        return;
      }

      if (!formData.date) {
        errorToast('Please select a date for sample collection.');
        return;
      }

      // Build the request body with proper animal-sample definition matching
      // Only include animals that are eligible for each specific sample definition
      const registeredAnimalsMap = buildRegisteredAnimalsMap(registeredAnimalsResponse);
      const sampleDefinitionsMap = buildSampleDefinitionsMap(
        selectedAnimals,
        tableData?.sample_definitions || [],
        registeredAnimalsMap,
        animalIdDefinitionMap
      );

      executeCreateSamples({
        path: { studyApiId },
        body: {
          sample_definitions: sampleDefinitionsMap,
          date: DateInputUtils.localDateTimeToISODateTime(formData.date),
          comments: formData.comments ?? null,
        },
      })
        .then(() => {
          successToast('Samples collected successfully!');
          // Navigate back to sample plan table
          navigate(samplesPlanRoute, { replace: true });
          navigate(backLink);
        })
        .catch(() => {
          errorToast('Failed to save sample collection. Please try again.');
        });
    },
    [
      studyApiId,
      selectedAnimals,
      sampleDefinitionIds,
      executeCreateSamples,
      navigate,
      samplesPlanRoute,
      backLink,
      tableData,
      animalIdDefinitionMap,
    ]
  );

  const handleGoBack = () => {
    navigate(
      RouteService.web({
        route: 'studies.samples.execute',
        path: { id: studyId },
      }).route,
      {
        state: {
          sampleDefinitionIds,
          studyApiId,
        },
      }
    );
  };

  if (isLoadingRequiredData(groupsLoading, samplePlanLoading, registeredAnimalsLoading, tableData)) {
    return <Loading />;
  }

  if (!hasValidExecutionState(studyApiId, sampleDefinitionIds, selectedAnimals)) {
    return (
      <div data-testid={testId}>
        <div className="ph4 pv3">
          <p>
            No sample collection data found. Please return to the sampling plan to select samples and animals to
            collect.
          </p>
          <button type="button" className="btn plain" onClick={() => navigate(backLink)}>
            Return to Sampling Plan
          </button>
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <div data-testid={testId}>
        <form onSubmit={formMethods.handleSubmit(handleSaveAndFinish)}>
          <div className="ph4 pb3">
            <div className="w-100 pb4">
              <Header mainHeaderText="Collect Samples" />
              <StepLinkList style={{ width: '305px' }} steps={steps} activeStep={currentStep} />
            </div>

            {tableData && (
              <div className="mb4">
                <SamplePlanTableProvider
                  sample_definitions={tableData.sample_definitions}
                  groups={tableData.groups}
                  registered_animals={tableData.registered_animals}
                  planned_animals={tableData.planned_animals}
                  metadata_glossary={sampleMetadataGlossaryResponse?.body ?? []}
                  sample_glossary={(sampleGlossaryResponse?.body as { data?: Array<TeamGlossary> })?.data ?? []}
                  displaySampleCounts={true}
                >
                  <SamplePlanTableWithBanner
                    selectedAnimals={selectedAnimals}
                    tableData={tableData}
                    registeredAnimalsResponse={registeredAnimalsResponse}
                    animalIdDefinitionMap={animalIdDefinitionMap}
                  />
                </SamplePlanTableProvider>
              </div>
            )}

            <div className="mb4">
              <DateTimePicker
                name="date"
                label="Collection Date & Time"
                required={true}
                includeTime={true}
                hint="Select when the samples were collected"
                testId={`${testId}__date-picker`}
              />
            </div>

            <div className="mb4">
              <label htmlFor="comments">Comments</label>
              <textarea
                {...formMethods.register('comments')}
                id="comments"
                className="input-reset ba b--black-20 pa2 mb2 db w-100 br2"
                rows={4}
                data-testid={`${testId}__comments`}
              />
            </div>

            <div className="flex">
              <Button
                primary
                submit
                disabled={isCreating || selectedAnimals.size === 0 || !watchedDate}
                className="mr3"
                data-testid={`${testId}__save-button`}
              >
                {isCreating ? 'Collecting...' : 'Collect'}
              </Button>
              <Button plain onClick={handleGoBack} disabled={isCreating} data-testid={`${testId}__cancel-button`}>
                Back
              </Button>
            </div>
          </div>
        </form>
      </div>
    </FormProvider>
  );
};

export default ExecuteSamplesCollectStep;
